import React, { useEffect } from 'react'

import { datadogLogs } from '@datadog/browser-logs'

import {
  Box,
  Color,
  Display,
  DisplayPosition,
  Image,
  withCitrusTheme,
  Visible,
  useTheme,
} from '@gousto-internal/citrus-react'
import { PlayPauseButton } from './PlayPauseButton'

import { VALUE_YOU_CAN_TASTE } from '../../../config'

import posterWebp from '../../../assets/ValueYouCanTasteSectionAssets/poster.webp'
import posterJpg from '../../../assets/ValueYouCanTasteSectionAssets/poster.jpg'
import { canUseWindow } from '@library/environment'

const GradientOverlay = withCitrusTheme(Box, (theme) => {
  const startColor = theme.color(Color.ColdGrey_800)
  const endColor = theme.color(Color.ColdGrey_900) + '00'

  return {
    background: `linear-gradient(to bottom, ${startColor}, ${endColor})`,
  }
})

export function Video() {
  const [showControls, setShowControls] = React.useState(false)
  const { theme } = useTheme()

  const videoRef = React.useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (!canUseWindow) return
    const video = videoRef.current
    if (!video) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video
              .play()
              .catch((error) =>
                datadogLogs.logger.error('ValueYouCanTaste video play failed', error),
              )
          } else {
            video.pause()
          }
        })
      },
      { threshold: 0.5 },
    )

    observer.observe(video)
    return () => observer.disconnect()
  }, [])

  return (
    <Box height={['560px', '100%']} position={DisplayPosition.Relative}>
      <video
        muted
        preload="none"
        loop
        poster={posterWebp}
        autoPlay
        playsInline
        style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
        ref={videoRef}
        onCanPlay={() => setShowControls(true)}
        aria-hidden="true"
      >
        <source
          src={VALUE_YOU_CAN_TASTE.mobileVideoURL}
          media={`(max-width: ${theme.tokens.breakpoints[1]})`}
          type="video/mp4"
        />
        <source
          src={VALUE_YOU_CAN_TASTE.desktopVideoURL}
          media={`(min-width: ${theme.tokens.breakpoints[1]})`}
          type="video/mp4"
        />
        {/* Fallback image in case browser does not support the video element*/}
        <Image src={posterJpg} srcSet={posterWebp} alt="video-poster" />
      </video>
      <Visible display={[Display.Block, Display.None]}>
        {/* Top position slightly above the video component to eliminate a tiny screen line not covered by the overlay at some screen sizes */}
        <GradientOverlay position={DisplayPosition.Absolute} top="-1px" height="60%" width="100%" />
      </Visible>
      {showControls && (
        <Box position={DisplayPosition.Absolute} bottom="16px" right="16px">
          <PlayPauseButton videoRef={videoRef} />
        </Box>
      )}
    </Box>
  )
}
